import axios, { AxiosResponse } from 'axios'

import { getToken } from '../adalConfig'

/**
 * We want to always make requests with the JWT token.
 * This may seem inefficient, but it turns out the getToken method will
 * use a cached token if it's available, and only make a request if the token
 * has or is about to expire.
 * This object just we just wrap all the default axios methods to include the Authorization header.
 * This approach was built based on the discussion here:
 * https://github.com/salvoravida/react-adal/issues/55
 *
 * If we are not using SSO (eg, development, testing, local) we just return axios.
 */
export const axiosWithAdal = process.env.NODE_ENV !== 'development' || process.env.REACT_APP_SSO_TEST_MODE === 'true'
  ? ({
    async get<Response>(url: string, config: Record<string, any>): Promise<AxiosResponse<Response>> {
      try {
        const token = await getToken()
        if (!token) {
          throw Error('Could not authenticate with Azure SSO')
        }

        return axios.get(url, {
          ...config, headers: {
            ...config.headers,
            Authorization: `Bearer ${token}`
          }
        })
      } catch (e) {
        throw Error(e)
      }
    },

    async post<Response>(url: string, body: any, config: Record<string, any>): Promise<AxiosResponse<Response>> {
      try {
        const token = await getToken()
        if (!token) {
          throw Error('Could not authenticate with Azure SSO')
        }

        return axios.post(url, body, {
          ...config, headers: {
            ...config.headers,
            Authorization: `Bearer ${token}`
          }
        })
      } catch (e) {
        throw Error(e)
      }
    },

    async delete<Response>(url: string, config: Record<string, any>): Promise<AxiosResponse<Response>> {
      try {
        const token = await getToken()
        if (!token) {
          throw Error('Could not authenticate with Azure SSO')
        }

        return axios.delete(url, {
          ...config, headers: {
            ...config.headers,
            Authorization: `Bearer ${token}`
          }
        })
      } catch (e) {
        throw Error(e)
      }
    },

    async put<Response>(url: string, body: any, config: Record<string, any>): Promise<AxiosResponse<Response>> {
      try {
        const token = await getToken()
        if (!token) {
          throw Error('Could not authenticate with Azure SSO')
        }

        return axios.put(url, body, {
          ...config, headers: {
            ...config.headers,
            Authorization: `Bearer ${token}`
          }
        })
      } catch (e) {
        throw Error(e)
      }
    }
  }) : ({
    async get<Response>(url: string, config: Record<string, any>): Promise<AxiosResponse<Response>> {
      return axios.get(url, config)
    },

    async post<Response>(url: string, body: any, config: Record<string, any>): Promise<AxiosResponse<Response>> {
      return axios.post(url, body, config)
    },

    async delete<Response>(url: string, config: Record<string, any>): Promise<AxiosResponse<Response>> {
      return axios.delete(url, config)
    },

    async put<Response>(url: string, body: any, config: Record<string, any>): Promise<AxiosResponse<Response>> {
      return axios.put(url, body, config)
    }
  })
